import { Checkbox, Icon, Loading } from '@smartgift/frontend-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import * as TableS from '@hero/components/molecules/Table/style';
import { contactServices } from '@hero/services';
import { cleanObjectEmptyFields, formatNumber } from '@hero/utils';

import * as S from '../Advance/style';

function RemoveContactCustomSelectionHead({ table }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [toggleSelectAll, setToggleSelectAll] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const showingSelectedItems = useMemo(() => {
    return table.getState()?.showingSelectedItems;
  }, [table.getState()?.showingSelectedItems]);

  const totalContactData = useMemo(() => {
    return table.getState()?.totalContactData;
  }, [table.getState()?.totalContactData]);

  const globalFilters = useMemo(() => {
    return table.getState()?.globalFilters;
  }, [table.getState()?.globalFilters]);

  const rowSelection = useMemo(() => {
    return table.getState()?.rowSelection;
  }, [table.getState()?.rowSelection]);

  const totalRowCount = useMemo(() => {
    return table.getState()?.pagination?.total;
  }, [table.getState()?.pagination?.total]);

  const selectedRowCount = useMemo(() => {
    return Object.keys(rowSelection || {}).length;
  }, [rowSelection]);

  const onToggleSelectAll = async () => {
    if (toggleSelectAll) {
      table.resetRowSelection();
    } else {
      const searchTerm = globalFilters.searchTerm;
      const tags = globalFilters.filters?.map(filter => filter.value);
      const ids = showingSelectedItems ? Object.keys(rowSelection) : undefined;

      setIsLoading(true);
      let filteredContactIds = [];

      if (tags?.length > 0 || searchTerm) {
        const data = await queryClient.fetchQuery(['selectAll', searchTerm, tags, ids], () => {
          return contactServices.selectAll(
            cleanObjectEmptyFields({
              filters: {
                searchTerm: globalFilters.searchTerm,
                tags: globalFilters.filters?.map(filter => filter.value),
                ids: showingSelectedItems ? Object.keys(rowSelection) : undefined
              }
            })
          );
        });

        filteredContactIds = data.docs.reduce((result, doc) => ({ ...result, [doc.id]: true }), {});
      } else {
        filteredContactIds = totalContactData.reduce(
          (result, doc) => ({ ...result, [doc]: true }),
          {}
        );
      }

      if (Object.keys(filteredContactIds).length) table.setRowSelection(filteredContactIds);

      setIsLoading(false);
    }

    setToggleSelectAll(!toggleSelectAll);
  };

  React.useEffect(() => {
    if (selectedRowCount === 0) {
      setToggleSelectAll(false);
    }
    if (selectedRowCount === totalRowCount) {
      setToggleSelectAll(true);
    }
  }, [selectedRowCount, totalRowCount]);

  return (
    <TableS.HeadSelected>
      <TableS.HeadCheckbox>
        <Checkbox
          {...{
            key: table.getState()?.pagination?.pageIndex,
            checked: table.getIsAllPageRowsSelected(),
            indeterminate: table.getIsSomePageRowsSelected(),
            onChange: table.getToggleAllPageRowsSelectedHandler()
          }}
        />
      </TableS.HeadCheckbox>
      <TableS.HeadSelectedItems>
        <TableS.HeadSelectedItems>
          <Icon name="contacts" px={32} space={7} />
          <S.SelectedContactText>
            {t(toggleSelectAll ? 'nContactAreSelected' : 'selectedRowCount', {
              selectedRowCount: selectedRowCount,
              selectedTypo: selectedRowCount,
              totalContactCount: totalRowCount
            })}
          </S.SelectedContactText>
        </TableS.HeadSelectedItems>
        <S.SelectAllArea>
          {!(!toggleSelectAll && totalRowCount - selectedRowCount <= 0) &&
            table.getPageCount() > 1 && (
              <S.SelectAllContactButton color="neutral" variant="link" onClick={onToggleSelectAll}>
                {t(toggleSelectAll ? 'clearSelection' : 'selectRemainingNType', {
                  totalContactCount: formatNumber(totalRowCount - selectedRowCount),
                  type: t('contacts').toLocaleLowerCase()
                })}
              </S.SelectAllContactButton>
            )}

          {isLoading && <Loading size="sm" type="ring" />}
        </S.SelectAllArea>
      </TableS.HeadSelectedItems>
    </TableS.HeadSelected>
  );
}

export { RemoveContactCustomSelectionHead };
